import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { AxiosResponse } from "axios";
import { Form, Formik, FormikHelpers } from "formik";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { useAppDispatch } from "../../Redux/hooks";
import {
  fetchActiveMaintenance,
  MaintenanceWindow,
} from "../../Redux/reducers/maintenanceSlice";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";

const DEFAULT_MESSAGE =
  "Scheduled maintenance. The app may be briefly unavailable during this window.";

type FormValues = {
  message: string;
  startsAt: DateTime | null;
  endsAt: DateTime | null;
};

type Status = "active" | "upcoming" | "ended";

const MAX_DAYS_AHEAD = 3;
// Small tolerance so a picker set to the current minute isn't rejected as past.
const PAST_TOLERANCE_MS = 60 * 1000;

const schema = yup.object().shape({
  message: yup.string().required("Message is required").trim(),
  startsAt: yup
    .mixed<DateTime>()
    .test("valid-start", "Start time is required", (v) =>
      Boolean(v && DateTime.isDateTime(v) && v.isValid)
    )
    .test(
      "not-past",
      "Start time cannot be in the past",
      (v) =>
        !v ||
        !DateTime.isDateTime(v) ||
        !v.isValid ||
        v.toMillis() >= DateTime.now().toMillis() - PAST_TOLERANCE_MS
    )
    .test(
      "within-window",
      `Start time must be within ${MAX_DAYS_AHEAD} days from now`,
      (v) =>
        !v ||
        !DateTime.isDateTime(v) ||
        !v.isValid ||
        v <= DateTime.now().plus({ days: MAX_DAYS_AHEAD })
    )
    .required("Start time is required"),
  endsAt: yup
    .mixed<DateTime>()
    .test("valid-end", "End time is required", (v) =>
      Boolean(v && DateTime.isDateTime(v) && v.isValid)
    )
    .test(
      "end-after-start",
      "End time must be after start time",
      function (v) {
        const start = this.parent.startsAt as DateTime | null | undefined;
        if (!v || !start) return true;
        return v > start;
      }
    )
    .test(
      "within-window",
      `End time must be within ${MAX_DAYS_AHEAD} days from now`,
      (v) =>
        !v ||
        !DateTime.isDateTime(v) ||
        !v.isValid ||
        v <= DateTime.now().plus({ days: MAX_DAYS_AHEAD })
    )
    .required("End time is required"),
});

const formatLocal = (iso: string) => {
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return iso;
  return dt.toLocaleString(DateTime.DATETIME_MED);
};

const computeStatus = (
  startsAt: string,
  endsAt: string,
  now: DateTime
): Status => {
  const start = DateTime.fromISO(startsAt);
  const end = DateTime.fromISO(endsAt);
  if (!start.isValid || !end.isValid) return "ended";
  if (now >= end) return "ended";
  if (now < start) return "upcoming";
  return "active";
};

const STATUS_META: Record<
  Status,
  { label: string; color: "success" | "warning" | "default" }
> = {
  active: { label: "Active", color: "success" },
  upcoming: { label: "Upcoming", color: "warning" },
  ended: { label: "Ended", color: "default" },
};

const MaintenanceSchedule = () => {
  const dispatch = useAppDispatch();
  const [schedules, setSchedules] = useState<MaintenanceWindow[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res: AxiosResponse = await http.get("maintenance-schedule");
      setSchedules(((res?.data?.data as MaintenanceWindow[]) ?? []) ?? []);
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const now = DateTime.now();
  const hasActiveOrUpcoming = useMemo(
    () =>
      schedules.some(
        (s) => computeStatus(s.startsAt, s.endsAt, now) !== "ended"
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schedules]
  );

  const tzName = DateTime.now().offsetNameLong;

  const openCreate = () => setModalOpen(true);
  const closeCreate = () => {
    if (submitting) return;
    setModalOpen(false);
  };

  const handleSubmit = async (
    values: FormValues,
    helpers: FormikHelpers<FormValues>
  ) => {
    try {
      setSubmitting(true);
      const body = {
        message: values.message.trim(),
        startsAt: values.startsAt!.toISO(),
        endsAt: values.endsAt!.toISO(),
      };
      await http.post("maintenance-schedule", body);
      toastMessage("success", "Maintenance window scheduled");
      helpers.resetForm();
      setModalOpen(false);
      await load();
      dispatch(fetchActiveMaintenance());
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setDeletingId(id);
      await http.delete(`maintenance-schedule/${id}`);
      toastMessage("success", "Maintenance window removed");
      await load();
      dispatch(fetchActiveMaintenance());
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1080, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 3,
          mb: 3,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
            Maintenance schedules
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Schedule maintenance windows for the platform. Users see the banner
            starting 12&nbsp;hours before the window opens, until they dismiss
            it or the window ends. Times are shown in your local timezone
            ({tzName}).
          </Typography>
        </Box>
        <Tooltip
          title={
            hasActiveOrUpcoming
              ? "There's already an active or upcoming schedule. Remove it before creating another."
              : ""
          }
        >
          <span style={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={openCreate}
              disabled={hasActiveOrUpcoming}
              sx={{
                whiteSpace: "nowrap",
                textTransform: "none",
                fontWeight: 600,
                px: 2.25,
                py: 1,
                borderRadius: 2,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              Create Schedule
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Paper variant="outlined" sx={{ mt: 3 }}>
        {loading && schedules.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={22} />
          </Box>
        ) : schedules.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No maintenance schedules yet.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Starts</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Ends</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.map((row) => {
                  const status = computeStatus(row.startsAt, row.endsAt, now);
                  const meta = STATUS_META[status];
                  const canDelete = status !== "ended";
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ opacity: status === "ended" ? 0.7 : 1 }}
                    >
                      <TableCell sx={{ width: 110 }}>
                        <Chip
                          size="small"
                          label={meta.label}
                          color={meta.color}
                          variant={
                            status === "ended" ? "outlined" : "filled"
                          }
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 360,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {row.message}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatLocal(row.startsAt)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatLocal(row.endsAt)}
                      </TableCell>
                      <TableCell align="right">
                        {canDelete && (
                          <Tooltip title="Remove schedule">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(row.id)}
                                disabled={deletingId === row.id}
                                aria-label="Remove schedule"
                              >
                                {deletingId === row.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DeleteOutlineRoundedIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={modalOpen}
        onClose={closeCreate}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 1 }}>
          <Box sx={{ flex: 1 }}>Create maintenance schedule</Box>
          <IconButton
            size="small"
            onClick={closeCreate}
            disabled={submitting}
            aria-label="Close"
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <Formik
          initialValues={{
            message: DEFAULT_MESSAGE,
            startsAt: null as DateTime | null,
            endsAt: null as DateTime | null,
          }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue, setFieldTouched }) => (
            <Form>
              <DialogContent>
                <Stack spacing={2.5}>
                  <TextField
                    label="Message"
                    name="message"
                    multiline
                    minRows={2}
                    fullWidth
                    value={values.message}
                    onChange={(e) => setFieldValue("message", e.target.value)}
                    onBlur={() => setFieldTouched("message", true)}
                    error={touched.message && Boolean(errors.message)}
                    helperText={touched.message && errors.message}
                  />
                  <DateTimePicker
                    label="Starts at"
                    value={values.startsAt}
                    onChange={(v) => setFieldValue("startsAt", v)}
                    disablePast
                    maxDateTime={DateTime.now().plus({
                      days: MAX_DAYS_AHEAD,
                    })}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        onBlur: () => setFieldTouched("startsAt", true),
                        error: touched.startsAt && Boolean(errors.startsAt),
                        helperText:
                          touched.startsAt && (errors.startsAt as string),
                      },
                    }}
                  />
                  <DateTimePicker
                    label="Ends at"
                    value={values.endsAt}
                    onChange={(v) => setFieldValue("endsAt", v)}
                    minDateTime={values.startsAt ?? DateTime.now()}
                    maxDateTime={DateTime.now().plus({
                      days: MAX_DAYS_AHEAD,
                    })}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        onBlur: () => setFieldTouched("endsAt", true),
                        error: touched.endsAt && Boolean(errors.endsAt),
                        helperText:
                          touched.endsAt && (errors.endsAt as string),
                      },
                    }}
                  />
                </Stack>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={closeCreate} disabled={submitting}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  startIcon={
                    submitting ? <CircularProgress size={14} /> : undefined
                  }
                >
                  Schedule
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default MaintenanceSchedule;
